import { useQuery } from '@tanstack/react-query';
import { profileService } from '@/services';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, BookOpen, FileText, FileCheck } from 'lucide-react';
import StatusWorkflowGrid from './StatusWorkflowGrid';

const ENTITY_TYPES = [
  { key: 'vendor', label: 'Vendor', icon: Users },
  { key: 'price_book', label: 'Price Book', icon: BookOpen },
  { key: 'invoice', label: 'Invoice', icon: FileText },
  { key: 'ticket', label: 'Field Ticket', icon: FileCheck },
  { key: 'quote_version', label: 'Quote', icon: FileText },
] as const;

export default function StatusWorkflowManager() {
  const { user } = useAuth();

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  if (profileLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-96 mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
          <div className="mt-6 space-y-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!profile?.company_id) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Status Workflows</CardTitle>
          <CardDescription>
            You must be associated with a company to manage status workflows.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Status Workflows</CardTitle>
        <CardDescription>
          Define statuses and valid workflow transitions for each entity type. Click a status or transition to edit it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="invoice" className="w-full">
          <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-6">
            {ENTITY_TYPES.map(({ key, label, icon: Icon }) => (
              <TabsTrigger key={key} value={key} className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {ENTITY_TYPES.map(({ key, label }) => (
            <TabsContent key={key} value={key}>
              <div className="space-y-4">
                <h3 className="text-lg font-medium">
                  Available Workflow Transitions from Each {label} Status
                </h3>
                <StatusWorkflowGrid entityType={key} companyId={profile.company_id} />
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
